<template>
  <div class="bdm-wrap">
    <!-- ══ HEADER ══ -->
    <div class="bdm-header">
      <div class="bdm-header-left">
        <div class="bdm-icon-wrap">
          <v-icon size="20" color="white">mdi-map-search</v-icon>
        </div>
        <div>
          <div class="bdm-title">ความหนาแน่นสาขาวิชา — รายจังหวัด</div>
          <div class="bdm-subtitle">สำรวจการกระจายตัวของสาขาวิชาในเชิงพื้นที่ทั่วประเทศ</div>
        </div>
      </div>
      <div class="bdm-header-right">
        <v-chip x-small outlined color="#e2e8f0" class="mr-2" style="color:#64748b">
          <v-icon size="11" left>mdi-calendar</v-icon>ปี {{ year }}
        </v-chip>
        <v-chip x-small color="#3b82f6" dark v-if="selectedBranch">
          <v-icon size="11" left>mdi-book-open-variant</v-icon>{{ selectedBranch }}
        </v-chip>
      </div>
    </div>

    <!-- ══ CONTROLS ══ -->
    <div class="bdm-controls">
      <!-- Search branch -->
      <div class="bdm-search-wrap">
        <div class="bdm-search-label">
          <v-icon size="13" color="#64748b">mdi-magnify</v-icon>
          <span>ค้นหาสาขาวิชา</span>
        </div>
        <div class="bdm-search-input-wrap">
          <input
            v-model="branchQuery"
            class="bdm-input"
            placeholder="เช่น ช่างยนต์, คอมพิวเตอร์..."
            @input="onBranchInput"
            @focus="showDropdown = true"
            @blur="hideDropdownDelay"
            autocomplete="off"
          />
          <button v-if="branchQuery" class="bdm-clear-btn" @click="clearBranch">
            <v-icon size="14" color="#9ca3af">mdi-close-circle</v-icon>
          </button>
        </div>
        <!-- Dropdown suggestions -->
        <div class="bdm-dropdown" v-if="showDropdown && branchSuggestions.length">
          <div
            v-for="b in branchSuggestions" :key="b.name"
            class="bdm-dropdown-item"
            @mousedown.prevent="selectBranch(b)"
          >
            <div class="bdm-drop-name">{{ b.name }}</div>
            <div class="bdm-drop-meta">
              <span class="bdm-drop-type">{{ b.subjectType }}</span>
              <span class="bdm-drop-count">{{ formatNum(b.total) }} คน · {{ b.provinceCount }} จังหวัด</span>
            </div>
          </div>
          <div v-if="!branchSuggestions.length" class="bdm-no-result">ไม่พบสาขาวิชา</div>
        </div>
      </div>

      <!-- Level filter -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">ระดับ</div>
        <div class="bdm-pill-group">
          <span
            v-for="lv in levelOptions" :key="lv.value"
            :class="['bdm-pill', { 'bdm-pill--active': levelFilter === lv.value }]"
            @click="levelFilter = lv.value"
          >{{ lv.label }}</span>
        </div>
      </div>

      <!-- Color scheme -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">สี</div>
        <div class="bdm-pill-group">
          <span
            v-for="s in schemeOptions" :key="s.value"
            :class="['bdm-pill', { 'bdm-pill--active': colorScheme === s.value }]"
            @click="colorScheme = s.value"
          >
            <span class="bdm-scheme-dot" :style="`background:${s.color}`"></span>
            {{ s.label }}
          </span>
        </div>
      </div>

      <!-- Metric selector -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">แสดง</div>
        <div class="bdm-pill-group">
          <span
            v-for="m in metricOptions" :key="m.value"
            :class="['bdm-pill', { 'bdm-pill--active': metric === m.value }]"
            @click="metric = m.value"
          >{{ m.label }}</span>
        </div>
      </div>
    </div>

    <!-- ══ MAIN CONTENT ══ -->
    <div class="bdm-body">
      <!-- LEFT: MAP -->
      <div class="bdm-map-panel">
        <!-- No branch selected overlay -->
        <div v-if="!selectedBranch" class="bdm-empty-state">
          <div class="bdm-empty-icon">
            <v-icon size="52" color="#cbd5e1">mdi-map-search-outline</v-icon>
          </div>
          <div class="bdm-empty-title">ค้นหาสาขาวิชาเพื่อดูการกระจายบนแผนที่</div>
          <div class="bdm-empty-sub">พิมพ์ชื่อสาขาวิชาในช่องด้านบน เช่น "ช่างยนต์" หรือ "การบัญชี"</div>
          <div class="bdm-quick-chips">
            <span
              v-for="q in quickBranches" :key="q"
              class="bdm-quick-chip"
              @click="quickSelect(q)"
            >{{ q }}</span>
          </div>
        </div>

        <!-- Map title bar (shown when a branch is selected) -->
        <div class="bdm-map-title-bar" v-if="selectedBranch">
          <span class="bdm-map-branch-name">{{ selectedBranch }}</span>
          <span class="bdm-map-stats">
            รวม {{ formatNum(branchTotal) }} คน · {{ activeProvinceCount }} จังหวัด
          </span>
          <div class="bdm-zoom-btns">
            <button class="bdm-zoom-btn" @click="mapZoomIn">+</button>
            <button class="bdm-zoom-btn" @click="mapZoomOut">−</button>
            <button class="bdm-zoom-btn" @click="mapFit">⊡</button>
          </div>
        </div>

        <!-- Leaflet map (จังหวัดจริงจาก GeoJSON แทน SVG วาดมือเดิม) -->
        <div class="bdm-leaflet-container" ref="mapEl"></div>

        <!-- Legend -->
        <div class="bdm-legend" v-if="selectedBranch">
          <div class="bdm-legend-title">{{ metricLabel }}</div>
          <div class="bdm-legend-bar">
            <div class="bdm-legend-gradient" :style="legendGradient"></div>
            <div class="bdm-legend-ticks">
              <span>0</span>
              <span>{{ formatNum(Math.round(maxProvValue / 2)) }}</span>
              <span>{{ formatNum(maxProvValue) }}</span>
            </div>
          </div>
        </div>

        <!-- Tooltip -->
        <div
          class="bdm-tooltip"
          v-if="tooltip.show"
          :style="`left:${tooltip.x}px;top:${tooltip.y}px`"
        >
          <div class="bdm-tt-header">
            <span class="bdm-tt-prov">{{ tooltip.province }}</span>
            <span class="bdm-tt-region" :style="`background:${tooltip.regionColor}`">{{ tooltip.region }}</span>
          </div>
          <div class="bdm-tt-body">
            <div class="bdm-tt-row">
              <span>นักเรียนรวม</span>
              <strong>{{ formatNum(tooltip.total) }} คน</strong>
            </div>
            <div class="bdm-tt-row">
              <span>ปวช.</span>
              <strong class="c-blue">{{ formatNum(tooltip.ch) }}</strong>
            </div>
            <div class="bdm-tt-row">
              <span>ปวส.</span>
              <strong class="c-purple">{{ formatNum(tooltip.hs) }}</strong>
            </div>
            <div class="bdm-tt-row">
              <span>วิทยาลัย</span>
              <strong>{{ tooltip.colleges }} แห่ง</strong>
            </div>
            <div class="bdm-tt-row" v-if="tooltip.rank">
              <span>อันดับ</span>
              <strong class="c-orange">{{ tooltip.rank }} / {{ activeProvinceCount }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: STATS PANEL -->
      <div class="bdm-stats-panel">
        <!-- Summary cards -->
        <div v-if="selectedBranch" class="bdm-stat-cards">
          <div class="bdm-stat-card bdm-stat-card--blue">
            <div class="bdm-stat-val">{{ formatNum(branchTotal) }}</div>
            <div class="bdm-stat-lbl">นักเรียนรวม</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--teal">
            <div class="bdm-stat-val">{{ activeProvinceCount }}</div>
            <div class="bdm-stat-lbl">จังหวัดที่มีสาขา</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--purple">
            <div class="bdm-stat-val">{{ branchColleges }}</div>
            <div class="bdm-stat-lbl">วิทยาลัย</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--orange">
            <div class="bdm-stat-val">{{ coveragePct }}%</div>
            <div class="bdm-stat-lbl">ครอบคลุมประเทศ</div>
          </div>
        </div>

        <!-- Province ranking table -->
        <div class="bdm-rank-panel" v-if="selectedBranch && provinceRanking.length">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#2563eb" class="mr-1">mdi-podium</v-icon>
            <span>อันดับจังหวัด</span>
            <v-spacer />
            <span class="bdm-rank-count">{{ provinceRanking.length }} จังหวัด</span>
          </div>
          <div class="bdm-rank-search">
            <input v-model="rankSearch" class="bdm-input bdm-input--sm" placeholder="กรองจังหวัด..." />
          </div>
          <div class="bdm-rank-list">
            <div
              v-for="(p, i) in filteredRanking" :key="p.name"
              class="bdm-rank-row"
              :class="{ 'bdm-rank-row--selected': selectedProvince === p.id }"
              @click="onClickProvByName(p.name)"
            >
              <div class="bdm-rank-num" :class="getRankClass(i)">{{ i + 1 }}</div>
              <div class="bdm-rank-info">
                <div class="bdm-rank-name">{{ p.name }}</div>
                <div class="bdm-rank-region" :style="`color:${getRegionColor(p.region)}`">
                  {{ p.region }}
                </div>
              </div>
              <div class="bdm-rank-bars">
                <div class="bdm-rank-bar-row">
                  <div class="bdm-rank-bar-bg">
                    <div
                      class="bdm-rank-bar-fill bdm-rank-bar-fill--blue"
                      :style="`width:${maxProvValue ? (p.ch/maxProvValue)*100 : 0}%`"
                    ></div>
                  </div>
                  <span class="bdm-rank-bar-val">{{ formatNum(p.ch) }}</span>
                </div>
                <div class="bdm-rank-bar-row">
                  <div class="bdm-rank-bar-bg">
                    <div
                      class="bdm-rank-bar-fill bdm-rank-bar-fill--purple"
                      :style="`width:${maxProvValue ? (p.hs/maxProvValue)*100 : 0}%`"
                    ></div>
                  </div>
                  <span class="bdm-rank-bar-val">{{ formatNum(p.hs) }}</span>
                </div>
              </div>
              <div class="bdm-rank-total">{{ formatNum(p.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Region breakdown -->
        <div class="bdm-region-panel" v-if="selectedBranch && regionBreakdown.length">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#0891b2" class="mr-1">mdi-map-legend</v-icon>
            <span>สรุปรายภาค</span>
          </div>
          <div class="bdm-region-list">
            <div v-for="r in regionBreakdown" :key="r.name" class="bdm-region-row">
              <div class="bdm-region-dot" :style="`background:${r.color}`"></div>
              <div class="bdm-region-info">
                <div class="bdm-region-name">{{ r.name }}</div>
                <div class="bdm-region-bar-bg">
                  <div class="bdm-region-bar-fill" :style="`width:${r.pct}%;background:${r.color}`"></div>
                </div>
              </div>
              <div class="bdm-region-vals">
                <div class="bdm-region-total">{{ formatNum(r.total) }}</div>
                <div class="bdm-region-pct">{{ r.pct }}%</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Compare branches (เปรียบเทียบ) -->
        <div class="bdm-compare-panel" v-if="compareList.length > 0 || selectedBranch">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#d97706" class="mr-1">mdi-compare</v-icon>
            <span>เปรียบเทียบสาขา</span>
            <v-spacer />
            <button class="bdm-add-btn" @click="addCompare" :disabled="!selectedBranch || compareList.includes(selectedBranch)">
              <v-icon size="12">mdi-plus</v-icon> เพิ่ม
            </button>
          </div>
          <div v-if="compareList.length === 0" class="bdm-compare-empty">
            คลิก "เพิ่ม" เพื่อเปรียบเทียบสาขาวิชาหลายสาขา
          </div>
          <div v-else class="bdm-compare-list">
            <div v-for="(b, i) in compareList" :key="b" class="bdm-compare-row">
              <span class="bdm-compare-dot" :style="`background:${compareColors[i]}`"></span>
              <span class="bdm-compare-name">{{ b }}</span>
              <span class="bdm-compare-val">{{ formatNum(getCompareBranchTotal(b)) }}</span>
              <button class="bdm-compare-remove" @click="removeCompare(b)">
                <v-icon size="11" color="#9ca3af">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <!-- Mini bar chart comparison -->
          <div v-if="compareList.length > 1" class="bdm-compare-chart">
            <apexchart
              type="bar" height="140"
              :options="compareChartOpts"
              :series="compareChartSeries"
            />
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!selectedBranch" class="bdm-stats-empty">
          <v-icon size="36" color="#cbd5e1">mdi-chart-bar-stacked</v-icon>
          <div>เลือกสาขาวิชาเพื่อดูสถิติ</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const GEOJSON_URL =
  "https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json";

const REGION_COLORS = {
  ภาคกลาง: '#2563eb', ภาคเหนือ: '#059669',
  ภาคตะวันออกเฉียงเหนือ: '#d97706', ภาคใต้: '#7c3aed',
  ภาคตะวันออก: '#0891b2', ภาคตะวันตก: '#db2777',
  กรุงเทพมหานคร: '#4f46e5'
}

// จังหวัดและภูมิภาค mapping
const PROVINCE_REGION = {
  'กรุงเทพมหานคร':'กรุงเทพมหานคร',
  'นนทบุรี':'ภาคกลาง','ปทุมธานี':'ภาคกลาง','สมุทรปราการ':'ภาคกลาง',
  'สมุทรสาคร':'ภาคกลาง','สมุทรสงคราม':'ภาคกลาง','นครปฐม':'ภาคกลาง',
  'สุพรรณบุรี':'ภาคกลาง','พระนครศรีอยุธยา':'ภาคกลาง','อ่างทอง':'ภาคกลาง',
  'สิงห์บุรี':'ภาคกลาง','ชัยนาท':'ภาคกลาง','ลพบุรี':'ภาคกลาง',
  'สระบุรี':'ภาคกลาง','นครนายก':'ภาคกลาง',
  'เชียงใหม่':'ภาคเหนือ','เชียงราย':'ภาคเหนือ','ลำปาง':'ภาคเหนือ',
  'ลำพูน':'ภาคเหนือ','แพร่':'ภาคเหนือ','น่าน':'ภาคเหนือ',
  'พะเยา':'ภาคเหนือ','แม่ฮ่องสอน':'ภาคเหนือ','ตาก':'ภาคเหนือ',
  'สุโขทัย':'ภาคเหนือ','พิษณุโลก':'ภาคเหนือ','กำแพงเพชร':'ภาคเหนือ',
  'พิจิตร':'ภาคเหนือ','อุตรดิตถ์':'ภาคเหนือ','นครสวรรค์':'ภาคเหนือ',
  'อุทัยธานี':'ภาคเหนือ',
  'นครราชสีมา':'ภาคตะวันออกเฉียงเหนือ','ขอนแก่น':'ภาคตะวันออกเฉียงเหนือ',
  'อุดรธานี':'ภาคตะวันออกเฉียงเหนือ','หนองคาย':'ภาคตะวันออกเฉียงเหนือ',
  'บึงกาฬ':'ภาคตะวันออกเฉียงเหนือ','สกลนคร':'ภาคตะวันออกเฉียงเหนือ',
  'นครพนม':'ภาคตะวันออกเฉียงเหนือ','มุกดาหาร':'ภาคตะวันออกเฉียงเหนือ',
  'กาฬสินธุ์':'ภาคตะวันออกเฉียงเหนือ','มหาสารคาม':'ภาคตะวันออกเฉียงเหนือ',
  'ร้อยเอ็ด':'ภาคตะวันออกเฉียงเหนือ','ยโสธร':'ภาคตะวันออกเฉียงเหนือ',
  'อุบลราชธานี':'ภาคตะวันออกเฉียงเหนือ','อำนาจเจริญ':'ภาคตะวันออกเฉียงเหนือ',
  'ชัยภูมิ':'ภาคตะวันออกเฉียงเหนือ','บุรีรัมย์':'ภาคตะวันออกเฉียงเหนือ',
  'สุรินทร์':'ภาคตะวันออกเฉียงเหนือ','ศรีสะเกษ':'ภาคตะวันออกเฉียงเหนือ',
  'เลย':'ภาคตะวันออกเฉียงเหนือ','หนองบัวลำภู':'ภาคตะวันออกเฉียงเหนือ',
  'ชลบุรี':'ภาคตะวันออก','ระยอง':'ภาคตะวันออก','จันทบุรี':'ภาคตะวันออก',
  'ตราด':'ภาคตะวันออก','ฉะเชิงเทรา':'ภาคตะวันออก','ปราจีนบุรี':'ภาคตะวันออก',
  'สระแก้ว':'ภาคตะวันออก',
  'กาญจนบุรี':'ภาคตะวันตก','ราชบุรี':'ภาคตะวันตก','เพชรบุรี':'ภาคตะวันตก',
  'ประจวบคีรีขันธ์':'ภาคตะวันตก',
  'นครศรีธรรมราช':'ภาคใต้','สุราษฎร์ธานี':'ภาคใต้','กระบี่':'ภาคใต้',
  'ตรัง':'ภาคใต้','พัทลุง':'ภาคใต้','สงขลา':'ภาคใต้','สตูล':'ภาคใต้',
  'ปัตตานี':'ภาคใต้','ยะลา':'ภาคใต้','นราธิวาส':'ภาคใต้',
  'พังงา':'ภาคใต้','ภูเก็ต':'ภาคใต้','ระนอง':'ภาคใต้','ชุมพร':'ภาคใต้'
}

export default {
  name: 'BranchDensityMap',

  props: {
    // studentData: Array of raw rows จาก rate_work_college_admin.php action:list
    studentData: { type: Array, default: () => [] },
    year: { type: String, default: '2568' }
  },

  data () {
    return {
      branchQuery: '',
      showDropdown: false,
      selectedBranch: '',
      levelFilter: 'all',
      colorScheme: 'blue',
      metric: 'total',
      rankSearch: '',
      selectedProvince: null,
      // leaflet
      leafletMap: null,
      leafletGeoLayer: null,
      geoData: null,
      // tooltip
      tooltip: { show: false, x: 0, y: 0, province: '', region: '', regionColor: '', total: 0, ch: 0, hs: 0, colleges: 0, rank: 0 },
      // compare
      compareList: [],
      compareColors: ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed'],
      levelOptions: [
        { label: 'ทั้งหมด', value: 'all' },
        { label: 'ปวช.', value: 'ch' },
        { label: 'ปวส.', value: 'hs' }
      ],
      schemeOptions: [
        { label: 'น้ำเงิน', value: 'blue', color: '#1e40af' },
        { label: 'เขียว', value: 'green', color: '#0f766e' },
        { label: 'ส้ม', value: 'orange', color: '#b45309' },
        { label: 'ม่วง', value: 'purple', color: '#6d28d9' }
      ],
      metricOptions: [
        { label: 'จำนวนนักเรียน', value: 'total' },
        { label: 'จำนวนวิทยาลัย', value: 'colleges' }
      ],
      quickBranches: ['ช่างยนต์', 'ช่างไฟฟ้า', 'การบัญชี', 'คอมพิวเตอร์', 'การตลาด', 'ช่างก่อสร้าง']
    }
  },

  computed: {
    // index ข้อมูลนักเรียนตามสาขาและจังหวัด
    branchProvinceIndex () {
      // { branchName: { provinceName: { ch, hs, total, colleges: Set } } }
      const idx = {}
      this.studentData.forEach(r => {
        const branch = r.name_type_course || r.rate_id_course_branch || 'ไม่ระบุ'
        const prov = r.province_name || 'ไม่ระบุ'
        if (!idx[branch]) idx[branch] = {}
        if (!idx[branch][prov]) idx[branch][prov] = { ch: 0, hs: 0, total: 0, colleges: new Set() }
        const v = (+r.rate_work_field_study_lavel_1 || 0) + (+r.rate_work_field_study_lavel_2 || 0) + (+r.rate_work_field_study_lavel_3 || 0)
        if (r.rate_work_college_id_class == 20) idx[branch][prov].ch += v
        else if (r.rate_work_college_id_class == 30) idx[branch][prov].hs += v
        idx[branch][prov].total += v
        idx[branch][prov].colleges.add(r.rate_work_college_code)
      })
      return idx
    },

    allBranchList () {
      const map = {}
      this.studentData.forEach(r => {
        const k = r.name_type_course || r.rate_id_course_branch || 'ไม่ระบุ'
        if (!map[k]) map[k] = { name: k, subjectType: r.subject_typeName || '', total: 0, colleges: new Set(), provinces: new Set() }
        const v = (+r.rate_work_field_study_lavel_1 || 0) + (+r.rate_work_field_study_lavel_2 || 0) + (+r.rate_work_field_study_lavel_3 || 0)
        map[k].total += v
        map[k].colleges.add(r.rate_work_college_code)
        map[k].provinces.add(r.province_name)
      })
      return Object.values(map).map(b => ({ ...b, colleges: b.colleges.size, provinceCount: b.provinces.size }))
        .sort((a, b) => b.total - a.total)
    },

    branchSuggestions () {
      if (!this.branchQuery) return this.allBranchList.slice(0, 12)
      const q = this.branchQuery.toLowerCase()
      return this.allBranchList.filter(b =>
        b.name.toLowerCase().includes(q) || b.subjectType.toLowerCase().includes(q)
      ).slice(0, 15)
    },

    // ข้อมูลสาขาที่เลือก รายจังหวัด
    currentBranchData () {
      if (!this.selectedBranch) return {}
      return this.branchProvinceIndex[this.selectedBranch] || {}
    },

    provinceRanking () {
      if (!this.selectedBranch) return []
      return Object.entries(this.currentBranchData)
        .map(([name, d]) => ({
          name,
          id: name,
          region: PROVINCE_REGION[name] || 'ไม่ระบุ',
          ch: d.ch, hs: d.hs,
          total: this.levelFilter === 'ch' ? d.ch : this.levelFilter === 'hs' ? d.hs : d.total,
          colleges: d.colleges.size
        }))
        .filter(p => p.total > 0)
        .sort((a, b) => b.total - a.total)
    },

    filteredRanking () {
      if (!this.rankSearch) return this.provinceRanking
      const q = this.rankSearch.toLowerCase()
      return this.provinceRanking.filter(p => p.name.toLowerCase().includes(q) || p.region.toLowerCase().includes(q))
    },

    maxProvValue () {
      if (!this.provinceRanking.length) return 1
      return Math.max(...this.provinceRanking.map(p => p.total)) || 1
    },

    activeProvinceCount () { return this.provinceRanking.length },

    branchTotal () {
      return this.provinceRanking.reduce((s, p) => s + p.total, 0)
    },

    branchColleges () {
      const s = new Set()
      Object.values(this.currentBranchData).forEach(d => d.colleges.forEach(c => s.add(c)))
      return s.size
    },

    coveragePct () {
      return Math.round((this.activeProvinceCount / 77) * 100)
    },

    regionBreakdown () {
      const map = {}
      this.provinceRanking.forEach(p => {
        const r = p.region || 'ไม่ระบุ'
        if (!map[r]) map[r] = { name: r, total: 0 }
        map[r].total += p.total
      })
      const grand = this.branchTotal || 1
      return Object.values(map).sort((a, b) => b.total - a.total).map(r => ({
        ...r, pct: Math.round((r.total / grand) * 100),
        color: REGION_COLORS[r.name] || '#6b7280'
      }))
    },

    legendGradient () {
      const schemes = {
        blue:   'linear-gradient(to right, #dbeafe, #1e40af)',
        green:  'linear-gradient(to right, #d1fae5, #0f766e)',
        orange: 'linear-gradient(to right, #fed7aa, #b45309)',
        purple: 'linear-gradient(to right, #ede9fe, #6d28d9)'
      }
      return `background:${schemes[this.colorScheme] || schemes.blue};border-radius:4px`
    },

    metricLabel () {
      return this.metric === 'colleges' ? 'จำนวนวิทยาลัย' : 'จำนวนนักเรียน'
    },

    compareChartSeries () {
      if (this.compareList.length < 2) return []
      // top 5 provinces for each compare branch
      const topProvs = new Set()
      this.compareList.forEach(b => {
        const data = this.branchProvinceIndex[b] || {}
        Object.entries(data).sort((a, b) => b[1].total - a[1].total).slice(0, 5).forEach(([p]) => topProvs.add(p))
      })
      const provList = [...topProvs].slice(0, 8)
      return this.compareList.map((b, i) => ({
        name: b,
        data: provList.map(p => {
          const d = (this.branchProvinceIndex[b] || {})[p]
          return d ? d.total : 0
        })
      }))
    },

    compareChartOpts () {
      const topProvs = new Set()
      this.compareList.forEach(b => {
        const data = this.branchProvinceIndex[b] || {}
        Object.entries(data).sort((a, b) => b[1].total - a[1].total).slice(0, 5).forEach(([p]) => topProvs.add(p))
      })
      const provList = [...topProvs].slice(0, 8)
      return {
        chart: { background: 'transparent', toolbar: { show: false } },
        colors: this.compareColors,
        plotOptions: { bar: { columnWidth: '70%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: { categories: provList, labels: { style: { fontFamily: 'Sarabun,sans-serif', fontSize: '10px' }, rotate: -30 } },
        yaxis: { labels: { formatter: v => v.toLocaleString('th-TH') } },
        legend: { fontFamily: 'Sarabun,sans-serif', fontSize: '11px' },
        grid: { borderColor: '#e5e7eb' },
        tooltip: { y: { formatter: v => v.toLocaleString('th-TH') + ' คน' } }
      }
    }
  },

  watch: {
    studentData () { this.refreshMapColors() },
    levelFilter () {
      this.selectedProvince = null
      this.refreshMapColors()
    },
    metric () { this.refreshMapColors() },
    colorScheme () { this.refreshMapColors() },
    selectedBranch () { this.refreshMapColors() }
  },

  mounted () {
    this.$nextTick(() => this.initLeafletMap())
  },

  beforeDestroy () {
    if (this.leafletMap) {
      this.leafletMap.remove()
      this.leafletMap = null
    }
  },

  methods: {
    formatNum (n) {
      if (n === null || n === undefined || n === 0) return '0'
      return (+n).toLocaleString('th-TH')
    },

    onBranchInput () {
      this.showDropdown = true
    },

    hideDropdownDelay () {
      setTimeout(() => { this.showDropdown = false }, 200)
    },

    selectBranch (b) {
      this.selectedBranch = b.name
      this.branchQuery = b.name
      this.showDropdown = false
      this.selectedProvince = null
    },

    clearBranch () {
      this.branchQuery = ''
      this.selectedBranch = ''
      this.selectedProvince = null
    },

    quickSelect (name) {
      const found = this.allBranchList.find(b => b.name.includes(name))
      if (found) {
        this.branchQuery = found.name
        this.selectBranch(found)
      } else {
        this.branchQuery = name
        this.selectedBranch = name
      }
    },

    getProvValue (provName) {
      if (!this.selectedBranch) return 0
      const d = this.currentBranchData[provName]
      if (!d) return 0
      if (this.metric === 'colleges') return d.colleges.size
      if (this.levelFilter === 'ch') return d.ch
      if (this.levelFilter === 'hs') return d.hs
      return d.total
    },

    interpolateColor (t) {
      // t: 0..1
      const schemes = {
        blue:   { from: [219, 234, 254], to: [30, 64, 175] },
        green:  { from: [209, 250, 229], to: [15, 118, 110] },
        orange: { from: [254, 215, 170], to: [180, 83, 9] },
        purple: { from: [237, 233, 254], to: [109, 40, 217] }
      }
      const s = schemes[this.colorScheme] || schemes.blue
      const r = Math.round(s.from[0] + (s.to[0] - s.from[0]) * t)
      const g = Math.round(s.from[1] + (s.to[1] - s.from[1]) * t)
      const b = Math.round(s.from[2] + (s.to[2] - s.from[2]) * t)
      return `rgb(${r},${g},${b})`
    },

    onClickProvByName (name) {
      this.selectedProvince = this.selectedProvince === name ? null : name
    },

    getRankClass (i) {
      if (i === 0) return 'bdm-rank-num--gold'
      if (i === 1) return 'bdm-rank-num--silver'
      if (i === 2) return 'bdm-rank-num--bronze'
      return ''
    },

    getRegionColor (name) { return REGION_COLORS[name] || '#6b7280' },

    // ── Leaflet map (จังหวัดจริงจาก GeoJSON) ──
    async initLeafletMap () {
      const el = this.$refs.mapEl
      if (!el || this.leafletMap) return
      this.leafletMap = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
        minZoom: 5,
        maxZoom: 10
      })
      this.mapFit()
      try {
        const res = await fetch(GEOJSON_URL)
        this.geoData = await res.json()
        this.renderGeoLayer()
      } catch (e) {
        console.error('GeoJSON load error:', e)
      }
    },

    renderGeoLayer () {
      if (!this.leafletMap || !this.geoData) return
      if (this.leafletGeoLayer) {
        this.leafletGeoLayer.remove()
        this.leafletGeoLayer = null
      }
      this.leafletGeoLayer = L.geoJSON(this.geoData, {
        style: f => this.geoStyle(f),
        onEachFeature: (f, layer) => this.bindLayerEvents(f, layer)
      }).addTo(this.leafletMap)
    },

    getFeatureName (feature) {
      const p = feature.properties || {}
      return p.name || p.prov_nameTH || p.NAME_1 || p.province || ''
    },

    geoStyle (feature) {
      const name = this.getFeatureName(feature)
      if (!this.selectedBranch) {
        return { fillColor: '#e2e8f0', fillOpacity: 0.7, color: '#fff', weight: 0.8 }
      }
      const val = this.getProvValue(name)
      const isSelected = this.selectedProvince === name
      if (val === 0) {
        return {
          fillColor: '#f1f5f9',
          fillOpacity: 0.7,
          color: isSelected ? '#1e40af' : '#fff',
          weight: isSelected ? 1.8 : 0.8
        }
      }
      const pct = val / this.maxProvValue
      return {
        fillColor: this.interpolateColor(pct),
        fillOpacity: 0.85,
        color: isSelected ? '#1e40af' : '#fff',
        weight: isSelected ? 1.8 : 0.8
      }
    },

    refreshMapColors () {
      if (!this.leafletGeoLayer) return
      this.leafletGeoLayer.eachLayer(layer => {
        if (layer.feature) layer.setStyle(this.geoStyle(layer.feature))
      })
    },

    bindLayerEvents (feature, layer) {
      layer.on({
        mousemove: e => {
          if (!this.selectedBranch) return
          const name = this.getFeatureName(feature)
          const d = this.currentBranchData[name]
          const rank = this.provinceRanking.findIndex(p => p.name === name) + 1
          const rect = this.$el.getBoundingClientRect()
          this.tooltip = {
            show: true,
            x: e.originalEvent.clientX - rect.left + 12,
            y: e.originalEvent.clientY - rect.top - 10,
            province: name,
            region: PROVINCE_REGION[name] || '',
            regionColor: REGION_COLORS[PROVINCE_REGION[name]] || '#6b7280',
            total: d ? (this.levelFilter === 'ch' ? d.ch : this.levelFilter === 'hs' ? d.hs : d.total) : 0,
            ch: d ? d.ch : 0,
            hs: d ? d.hs : 0,
            colleges: d ? d.colleges.size : 0,
            rank: rank || null
          }
        },
        mouseout: () => {
          this.tooltip = { ...this.tooltip, show: false }
        },
        click: () => {
          const name = this.getFeatureName(feature)
          this.onClickProvByName(name)
        }
      })
    },

    mapZoomIn () { this.leafletMap && this.leafletMap.zoomIn() },
    mapZoomOut () { this.leafletMap && this.leafletMap.zoomOut() },
    mapFit () {
      if (!this.leafletMap) return
      this.leafletMap.fitBounds(
        [[5.5, 97.3], [20.5, 105.7]],
        { padding: [8, 8] }
      )
    },

    // ── Compare ──
    addCompare () {
      if (this.selectedBranch && !this.compareList.includes(this.selectedBranch) && this.compareList.length < 5) {
        this.compareList.push(this.selectedBranch)
      }
    },
    removeCompare (b) { this.compareList = this.compareList.filter(x => x !== b) },
    getCompareBranchTotal (b) {
      return Object.values(this.branchProvinceIndex[b] || {}).reduce((s, d) => s + d.total, 0)
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap');

* { box-sizing: border-box; }
.bdm-wrap {
  font-family: 'Sarabun', sans-serif;
  background: #fff; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  overflow: visible; position: relative;
  border: 1px solid #e2e8f0;
}

/* ══ HEADER ══ */
.bdm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #f1f5f9;
  background: #1e293b;
  border-radius: 13px 13px 0 0;
}
.bdm-header-left { display: flex; align-items: center; gap: 12px; }
.bdm-icon-wrap { width: 36px; height: 36px; background: rgba(255,255,255,.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.bdm-title { font-size: 0.95rem; font-weight: 700; color: #fff; }
.bdm-subtitle { font-size: 0.68rem; color: rgba(255,255,255,.7); margin-top: 1px; }
.bdm-header-right { display: flex; align-items: center; gap: 6px; }

/* ══ CONTROLS ══ */
.bdm-controls {
  display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px;
  padding: 12px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
}
.bdm-search-wrap { position: relative; min-width: 260px; }
.bdm-search-label { display: flex; align-items: center; gap: 4px; font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 4px; }
.bdm-search-input-wrap { position: relative; }
.bdm-input {
  width: 100%; padding: 7px 32px 7px 10px; border: 1.5px solid #e2e8f0;
  border-radius: 8px; font-family: 'Sarabun', sans-serif; font-size: 0.82rem;
  outline: none; background: #fff; transition: border-color .15s;
}
.bdm-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.bdm-input--sm { padding: 5px 10px; font-size: 0.76rem; }
.bdm-clear-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; padding: 0; }
.bdm-dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); z-index: 200; max-height: 280px; overflow-y: auto;
}
.bdm-dropdown-item { padding: 9px 12px; cursor: pointer; transition: background .1s; }
.bdm-dropdown-item:hover { background: #eff6ff; }
.bdm-drop-name { font-size: 0.82rem; font-weight: 600; color: #1e293b; }
.bdm-drop-meta { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.bdm-drop-type { font-size: 0.65rem; color: #6366f1; background: #eef2ff; padding: 1px 6px; border-radius: 4px; }
.bdm-drop-count { font-size: 0.65rem; color: #9ca3af; }
.bdm-no-result { padding: 12px; text-align: center; color: #9ca3af; font-size: 0.8rem; }

.bdm-filter-group { display: flex; flex-direction: column; gap: 4px; }
.bdm-filter-label { font-size: 0.63rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }
.bdm-pill-group { display: flex; gap: 4px; }
.bdm-pill { padding: 4px 10px; border-radius: 6px; font-size: 0.72rem; cursor: pointer; background: #f1f5f9; color: #64748b; transition: all .15s; display: flex; align-items: center; gap: 5px; font-weight: 500; }
.bdm-pill:hover { background: #e0e7ff; color: #4338ca; }
.bdm-pill--active { background: #2563eb; color: #fff; }
.bdm-scheme-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* ══ BODY ══ */
.bdm-body { display: flex; gap: 0; min-height: 600px; }

/* ══ MAP PANEL ══ */
.bdm-map-panel { flex: 1.4; position: relative; min-height: 600px; border-right: 1px solid #f1f5f9; background: #fafcff; overflow: hidden; display: flex; flex-direction: column; }

/* Empty state (overlay on top of the always-mounted leaflet map) */
.bdm-empty-state { position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 30px; text-align: center; background: #fafcff; }
.bdm-empty-icon { margin-bottom: 12px; }
.bdm-empty-title { font-size: 0.9rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
.bdm-empty-sub { font-size: 0.76rem; color: #64748b; max-width: 280px; line-height: 1.5; margin-bottom: 16px; }
.bdm-quick-chips { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-bottom: 20px; }
.bdm-quick-chip { padding: 5px 12px; background: #eff6ff; color: #2563eb; border-radius: 999px; font-size: 0.75rem; font-weight: 600; cursor: pointer; border: 1px solid #bfdbfe; transition: all .15s; }
.bdm-quick-chip:hover { background: #2563eb; color: #fff; }
/* Map title bar */
.bdm-map-title-bar { display: flex; align-items: center; padding: 8px 14px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; gap: 8px; z-index: 2; position: relative; }
.bdm-map-branch-name { font-size: 0.82rem; font-weight: 700; color: #1e293b; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-map-stats { font-size: 0.68rem; color: #64748b; white-space: nowrap; }
.bdm-zoom-btns { display: flex; gap: 4px; }
.bdm-zoom-btn { width: 24px; height: 24px; border: 1px solid #e2e8f0; background: #fff; border-radius: 5px; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; transition: background .1s; }
.bdm-zoom-btn:hover { background: #eff6ff; color: #2563eb; }

/* Leaflet map container */
.bdm-leaflet-container { flex: 1; min-height: 0; position: relative; z-index: 1; background: #eef2f7; }
.bdm-leaflet-container >>> .leaflet-container { background: #eef2f7; font-family: 'Sarabun', sans-serif; }

/* Legend */
.bdm-legend { padding: 8px 14px 10px; border-top: 1px solid #e2e8f0; background: #f8fafc; }
.bdm-legend-title { font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 5px; }
.bdm-legend-bar { display: flex; flex-direction: column; gap: 3px; }
.bdm-legend-gradient { height: 10px; width: 100%; border-radius: 5px; }
.bdm-legend-ticks { display: flex; justify-content: space-between; font-size: 0.65rem; color: #9ca3af; }

/* Tooltip */
.bdm-tooltip {
  position: absolute; background: #1e293b; color: #fff;
  border-radius: 10px; padding: 10px 14px; font-size: 0.75rem;
  pointer-events: none; z-index: 300; min-width: 160px;
  box-shadow: 0 8px 24px rgba(0,0,0,.25);
}
.bdm-tt-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.bdm-tt-prov { font-weight: 700; font-size: 0.82rem; }
.bdm-tt-region { font-size: 0.62rem; padding: 1px 6px; border-radius: 999px; color: #fff; opacity: .9; }
.bdm-tt-body { display: flex; flex-direction: column; gap: 4px; }
.bdm-tt-row { display: flex; justify-content: space-between; gap: 16px; }
.bdm-tt-row span { color: rgba(255,255,255,.65); }
.bdm-tt-row strong { font-weight: 700; }

/* ══ STATS PANEL ══ */
.bdm-stats-panel { width: 310px; flex-shrink: 0; display: flex; flex-direction: column; gap: 0; overflow-y: auto; max-height: 700px; }

/* Summary cards */
.bdm-stat-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 12px; }
.bdm-stat-card { border-radius: 10px; padding: 10px 12px; }
.bdm-stat-card--blue   { background: #eff6ff; border: 1px solid #bfdbfe; }
.bdm-stat-card--teal   { background: #f0fdfa; border: 1px solid #99f6e4; }
.bdm-stat-card--purple { background: #f5f3ff; border: 1px solid #ddd6fe; }
.bdm-stat-card--orange { background: #fff7ed; border: 1px solid #fed7aa; }
.bdm-stat-val { font-size: 1.3rem; font-weight: 800; color: #1e293b; line-height: 1.1; }
.bdm-stat-lbl { font-size: 0.65rem; color: #64748b; font-weight: 600; margin-top: 2px; }

/* Rank panel */
.bdm-rank-panel, .bdm-region-panel, .bdm-compare-panel { border-top: 1px solid #f1f5f9; }
.bdm-rank-header { display: flex; align-items: center; padding: 9px 14px; font-size: 0.78rem; font-weight: 700; color: #1e293b; background: #f8fafc; border-bottom: 1px solid #f1f5f9; }
.bdm-rank-count { font-size: 0.68rem; color: #64748b; }
.bdm-rank-search { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
.bdm-rank-list { max-height: 260px; overflow-y: auto; }
.bdm-rank-row { display: flex; align-items: center; gap: 8px; padding: 7px 12px; cursor: pointer; transition: background .1s; }
.bdm-rank-row:hover { background: #f0f9ff; }
.bdm-rank-row--selected { background: #eff6ff !important; }
.bdm-rank-num { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.62rem; font-weight: 700; flex-shrink: 0; background: #f1f5f9; color: #64748b; }
.bdm-rank-num--gold   { background: #fef3c7; color: #b45309; }
.bdm-rank-num--silver { background: #f1f5f9; color: #475569; }
.bdm-rank-num--bronze { background: #fef3c7; color: #92400e; }
.bdm-rank-info { flex: 1; min-width: 0; }
.bdm-rank-name   { font-size: 0.76rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-rank-region { font-size: 0.63rem; font-weight: 600; }
.bdm-rank-bars { flex: 1.2; display: flex; flex-direction: column; gap: 2px; }
.bdm-rank-bar-row { display: flex; align-items: center; gap: 4px; }
.bdm-rank-bar-bg { flex: 1; height: 4px; background: #e5e7eb; border-radius: 2px; overflow: hidden; }
.bdm-rank-bar-fill { height: 4px; border-radius: 2px; transition: width .4s; }
.bdm-rank-bar-fill--blue   { background: #3b82f6; }
.bdm-rank-bar-fill--purple { background: #8b5cf6; }
.bdm-rank-bar-val { font-size: 0.62rem; color: #64748b; min-width: 34px; text-align: right; }
.bdm-rank-total { font-size: 0.76rem; font-weight: 700; color: #1e293b; min-width: 42px; text-align: right; }

/* Region breakdown */
.bdm-region-list { padding: 8px 12px; display: flex; flex-direction: column; gap: 6px; }
.bdm-region-row { display: flex; align-items: center; gap: 8px; }
.bdm-region-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.bdm-region-info { flex: 1; min-width: 0; }
.bdm-region-name { font-size: 0.73rem; font-weight: 600; color: #1e293b; }
.bdm-region-bar-bg { height: 5px; background: #e5e7eb; border-radius: 3px; overflow: hidden; margin-top: 2px; }
.bdm-region-bar-fill { height: 5px; border-radius: 3px; transition: width .4s; }
.bdm-region-vals { text-align: right; }
.bdm-region-total { font-size: 0.73rem; font-weight: 700; color: #1e293b; }
.bdm-region-pct   { font-size: 0.63rem; color: #9ca3af; }

/* Compare */
.bdm-compare-panel { border-top: 1px solid #f1f5f9; }
.bdm-add-btn { display: flex; align-items: center; gap: 3px; font-size: 0.68rem; background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; border-radius: 6px; padding: 2px 8px; cursor: pointer; font-family: 'Sarabun', sans-serif; }
.bdm-add-btn:disabled { opacity: .4; cursor: not-allowed; }
.bdm-compare-empty { padding: 10px 14px; font-size: 0.73rem; color: #9ca3af; }
.bdm-compare-list { padding: 6px 12px; display: flex; flex-direction: column; gap: 4px; }
.bdm-compare-row { display: flex; align-items: center; gap: 7px; font-size: 0.75rem; }
.bdm-compare-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.bdm-compare-name { flex: 1; color: #1e293b; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-compare-val { font-size: 0.73rem; color: #64748b; min-width: 50px; text-align: right; }
.bdm-compare-remove { background: none; border: none; cursor: pointer; padding: 0; }
.bdm-compare-chart { padding: 0 8px 10px; }

/* Stats empty */
.bdm-stats-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #9ca3af; font-size: 0.8rem; padding: 40px 20px; }

/* Colors */
.c-blue   { color: #2563eb !important; }
.c-purple { color: #7c3aed !important; }
.c-orange { color: #ea580c !important; }

/* Responsive */
@media (max-width: 820px) {
  .bdm-body { flex-direction: column; }
  .bdm-stats-panel { width: 100%; max-height: none; border-top: 1px solid #e2e8f0; }
  .bdm-map-panel { min-height: 420px; }
}
@media (max-width: 560px) {
  .bdm-controls { gap: 10px; }
  .bdm-search-wrap { min-width: 100%; }
}
</style>
